import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface StepIndicatorProps {
  currentStep: number;
  onStepClick?: (step: number) => void;
}

const steps = [
  { number: 1, label: "Library Info" },
  { number: 2, label: "Admin Account" },
  { number: 3, label: "Contact Info" },
  { number: 4, label: "Review" },
];

export default function StepIndicator({ currentStep, onStepClick }: StepIndicatorProps) {
  return (
    <nav aria-label="Onboarding progress" className="mb-8">
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.number;
          const isCurrent = currentStep === step.number;
          const isClickable = isCompleted && onStepClick;

          return (
            <li key={step.number} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick(step.number)}
                  disabled={!isClickable}
                  className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200",
                    isCompleted && "bg-primary text-white cursor-pointer hover:bg-primary-600",
                    isCurrent && "bg-primary text-white ring-[3px] ring-primary/20",
                    !isCompleted && !isCurrent && "bg-slate-100 text-slate-400 cursor-default"
                  )}
                  aria-current={isCurrent ? "step" : undefined}
                  aria-label={`Step ${step.number}: ${step.label}${isCompleted ? " (completed)" : isCurrent ? " (current)" : ""}`}
                >
                  {isCompleted ? <Check className="h-4 w-4" strokeWidth={2.5} /> : step.number}
                </button>
                <span
                  className={cn(
                    "mt-2 text-xs font-medium whitespace-nowrap",
                    isCompleted || isCurrent ? "text-slate-900" : "text-slate-400"
                  )}
                >
                  {step.label}
                </span>
              </div>

              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "h-[2px] flex-1 mx-3 -mt-5 rounded-full transition-colors duration-200",
                    currentStep > step.number ? "bg-primary" : "bg-slate-200"
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
